import { useEffect } from "react";
import PropTypes from "prop-types";
import { useLazyGetClaimsSearchQuery } from "../../slices/dataviewApiSlice";

const ApiFetch = ({
    selectedIsfOrganizationIds,
    filterStatusValues,
    initialPolicyDivision,
    claimCoverageTypes,
    setClaimApiFetching,
    setClaimApiError,
    setClaimApiFulfilled,
    reportingPeriodValue,
    setReportTableData,
    shouldFetchApi,
    useFixedEffectiveDates,
}) => {
    const [getClaimsSearch] = useLazyGetClaimsSearchQuery();

    const fetchAllClaimData = async () => {
        // Don't fetch if we already have cached data
        if (!shouldFetchApi) {
            return;
        }

        setClaimApiFetching(true);
        setClaimApiError(null);

        const pageSize = 1000;
        const concurrencyLimit = 2; // max concurrent page fetches
        const maxAttemptsPerPage = 2; // Total attempts for a given page before erroring out entire fetch

        const buildFilters = (pageNumber) => ({
            organizationId: selectedIsfOrganizationIds,
            claimCoverageTypes,
            effectiveStartDate: useFixedEffectiveDates ? "1900-01-01" : reportingPeriodValue?.minimumStartDate,
            effectiveEndDate: useFixedEffectiveDates ? "1900-01-01" : reportingPeriodValue?.maximumEndDate,
            claimStatus: filterStatusValues,
            groupAgreementDivision:
                initialPolicyDivision.length > 500 ? "" : initialPolicyDivision,
            startIndex: 0,
            startPage: pageNumber,
            count: pageSize,
        });

        const fetchPage = async (pageNumber) => {
            const result = await getClaimsSearch({
                filters: buildFilters(pageNumber),
                formatType: "Snapshot",
            }).unwrap();

            return {
                pageNumber,
                claims: result?.claimsData ?? [],
                totalResults: result?.searchResultMetadata?.totalResults ?? 0,
            };
        };

        const fetchPageWithRetry = async (pageNumber) => {
            let attempt = 1;
            while (attempt <= maxAttemptsPerPage) {
                try {
                    return await fetchPage(pageNumber);
                } catch (fetchError) {
                    if (attempt === maxAttemptsPerPage) {
                        throw fetchError;
                    }
                    console.warn(
                        `Retrying page ${pageNumber} (attempt ${attempt + 1} of ${maxAttemptsPerPage})`
                    );
                    attempt += 1;
                }
            }
            return null;
        };

        try {
            const pageResults = new Map();

            // Always fetch the first page sequentially to determine total pages
            const firstPage = await fetchPageWithRetry(1);
            pageResults.set(1, firstPage.claims);

            // Use API metadata when available; otherwise fall back to what we actually received
            const normalisedTotalResults =
                typeof firstPage.totalResults === "number" && firstPage.totalResults > 0
                    ? firstPage.totalResults
                    : firstPage.claims.length;
            // Always schedule at least one page and round up so partial pages are still fetched
            const totalPages = Math.max(1, Math.ceil(normalisedTotalResults / pageSize));

            if (totalPages > 1) {
                const remainingPages = Array.from(
                    { length: totalPages - 1 },
                    (_, index) => index + 2
                );

                let pageCursor = 0;
                const fetcher = async () => {
                    // Fetch subsequent pages while respecting concurrency limit
                    while (pageCursor < remainingPages.length) {
                        const currentIndex = pageCursor;
                        pageCursor += 1;
                        const pageNumber = remainingPages[currentIndex];
                        const page = await fetchPageWithRetry(pageNumber);
                        pageResults.set(pageNumber, page.claims);
                    }
                };


                const fetcherCount = Math.min(concurrencyLimit, remainingPages.length);

                // Launch up to concurrencyLimit fetcher loops in parallel and wait for all to finish
                // Each fetcher fetches pages one at a time until there are no pages left, using 
                // shared pageCursor to coordinate
                await Promise.all(
                    Array.from({ length: fetcherCount }, () => fetcher())
                );
            }

            // Flatten pages in order while deduplicating by claimId
            const dedupedClaims = [];
            const seenClaimIds = new Set();
            for (let page = 1; page <= totalPages; page += 1) {
                const claimsForPage = pageResults.get(page) ?? [];
                claimsForPage.forEach((claim) => {
                    if (!seenClaimIds.has(claim.claimId)) {
                        seenClaimIds.add(claim.claimId);
                        dedupedClaims.push(claim);
                    }
                });
            }

            setReportTableData(dedupedClaims);
            setClaimApiFulfilled("fulfilled");
            setClaimApiFetching(false);
        } catch (error) {
            console.error("Error fetching claim data:", error);
            setClaimApiFetching(false);
            setClaimApiError(error);
            setClaimApiFulfilled("error");
        }
    };

    useEffect(() => {
        if (shouldFetchApi) {
            fetchAllClaimData();
        }
    }, [shouldFetchApi]);

    return null;
};

// PropTypes: Runtime type validation for component props
ApiFetch.propTypes = {
    selectedIsfOrganizationIds: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string,
    ]).isRequired,

    // Filter arrays
    filterStatusValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    initialPolicyDivision: PropTypes.array.isRequired,
    claimCoverageTypes: PropTypes.arrayOf(PropTypes.string).isRequired,

    // Date range object with minimumStartDate and maximumEndDate
    reportingPeriodValue: PropTypes.shape({
        minimumStartDate: PropTypes.string.isRequired,
        maximumEndDate: PropTypes.string.isRequired,
    }),

    // State setter callbacks
    setClaimApiFetching: PropTypes.func.isRequired,
    setClaimApiError: PropTypes.func.isRequired,
    setClaimApiFulfilled: PropTypes.func.isRequired,
    setReportTableData: PropTypes.func.isRequired,
    shouldFetchApi: PropTypes.bool.isRequired,
    // When true, sends 1900-01-01 for both effective dates (Disability Snapshot behaviour)
    useFixedEffectiveDates: PropTypes.bool,
};

export default ApiFetch;